import {formatMoney} from '@shopify/theme-currency';
import * as Sqrl from 'squirrelly';

import * as a11y from '../vendor/theme-scripts/theme-a11y';
import slideToggle from '../util/slide-toggle';
import slideUp from '../util/slide-up';
import slideDown from '../util/slide-down';
import debounce from '../util/debounce';
import FetchError from '../util/fetch-error';
import PairWithProduct from '../features/render-pair-product';

import isVisible from './is-visible';
import QuantityCounter from './quantity-handle';

const classes = {
  active: 'is-active',
  added: 'is-added',
  disabled: 'is-disabled',
  drawerVisible: 'drawer--visible',
  error: 'has-error',
  headerStuck: 'js__header__stuck',
  hidden: 'is-hidden',
  htmlClasses: 'has-open-cart-dropdown',
  loading: 'is-loading',
  open: 'is-open',
  success: 'is-success',
  visible: 'is-visible',
  focused: 'is-focused',
};

const attributes = {
  transparent: 'data-header-transparent',
  upsellButton: 'data-upsell-btn',
};

const selectors = {
  additionalCheckoutButtons: '.additional-checkout-buttons',
  burgerButton: '[data-drawer-toggle]',
  buttonAddToCart: '[data-add-to-cart]',
  buttonHolder: '[data-foot-holder]',
  buttonSkipPairProduct: '[data-skip-pair-product]',
  cartCloseError: '[data-cart-error-close]',
  cartCount: '[data-cart-count]',
  cartCountValue: 'data-cart-count',
  cartDiscountsHolder: '[data-cart-discounts-holder]',
  cartDropdown: '[data-cart-dropdown]',
  cartDropdownBody: '[data-cart-dropdown-body]',
  cartEmpty: '[data-cart-empty]',
  cartErrors: '[data-cart-errors]',
  cartItemRemove: '[data-item-remove]',
  cartMessage: '[data-cart-message]',
  cartMessageValue: 'data-cart-message',
  cartOriginalTotal: '[data-cart-original-total]',
  cartOriginaTotalPrice: '[data-cart-original-total-price]',
  cartProgress: '[data-cart-progress]',
  cartToggleElement: '[data-cart-toggle]',
  cartTotal: '[data-cart-total]',
  cartTotalDiscountsTemplate: '[data-cart-total-discount]',
  cartWidget: '[data-cart-widget]',
  clickedElementForExpanding: '[data-expand-button]',
  errorMessage: '[data-error-message]',
  formCloseError: '[data-close-error]',
  formErrorsContainer: '[data-cart-errors-container]',
  headerWrapper: '[data-header-wrapper]',
  item: '[data-item]',
  itemsHolder: '[data-items-holder]',
  leftToSpend: '[data-left-to-spend]',
  navDrawer: '[data-drawer]',
  outerSection: '[data-section-id]',
  pairProducts: '[data-pair-products]',
  pairProductsHolder: '[data-pair-products-holder]',
  pairProductsScript: '[data-pair-products-script]',
  productIDAttribute: 'data-product-id',
  quickAddHolder: '[data-quick-add-holder]',
  upsellHolder: '[data-upsell-holder]',
  upsellModal: '[data-upsell-modal]',
};

const settings = {
  cartDrawerEnabled: window.theme.settings.cartDrawerEnabled,
  times: {
    timeoutAddProduct: 1000,
    closeDropdownAfter: 5000,
  },
};

class CartDrawer {
  constructor() {
    if (window.location.pathname.endsWith('/password')) {
      return;
    }

    this.init();
  }

  init() {
    // DOM Elements
    this.html = document.documentElement;
    this.body = document.body;
    this.cartDropdown = document.querySelector(selectors.cartDropdown);
    this.cartDropdownBody = document.querySelector(selectors.cartDropdownBody);
    this.cartEmpty = document.querySelector(selectors.cartEmpty);
    this.buttonHolder = document.querySelector(selectors.buttonHolder);
    this.itemsHolder = document.querySelector(selectors.itemsHolder);
    this.items = document.querySelectorAll(selectors.item);
    this.counterHolders = document.querySelectorAll(selectors.cartCount);
    this.cartTotal = document.querySelector(selectors.cartTotal);
    this.cartMessage = document.querySelectorAll(selectors.cartMessage);
    this.cartOriginalTotal = document.querySelector(selectors.cartOriginalTotal);
    this.cartOriginaTotalPrice = document.querySelector(selectors.cartOriginaTotalPrice);
    this.cartDiscountHolder = document.querySelector(selectors.cartDiscountsHolder);
    this.clickedElementForExpanding = document.querySelectorAll(selectors.clickedElementForExpanding);
    this.cartTotalDiscountTemplate = document.querySelector(selectors.cartTotalDiscountsTemplate).innerHTML;
    this.cartErrorHolder = document.querySelector(selectors.cartErrors);
    this.cartCloseErrorMessage = document.querySelector(selectors.cartCloseError);
    this.headerWrapper = document.querySelector(selectors.headerWrapper);
    this.accessibility = a11y;
    this.navDrawer = document.querySelector(selectors.navDrawer);
    this.pairProductsHolder = document.querySelector(selectors.pairProductsHolder);
    this.pairProducts = document.querySelector(selectors.pairProducts);

    this.form = null;

    this.build = this.build.bind(this);

    // AJAX request
    this.addToCart = this.addToCart.bind(this);
    this.updateCart = this.updateCart.bind(this);

    // Cart events
    this.openCartDropdown = this.openCartDropdown.bind(this);
    this.closeCartDropdown = this.closeCartDropdown.bind(this);
    this.toggleCartDropdown = this.toggleCartDropdown.bind(this);

    // Checking
    this.hasItemsInCart = this.hasItemsInCart.bind(this);

    // Set classes
    this.toggleClassesOnContainers = this.toggleClassesOnContainers.bind(this);

    // Flags
    this.cartDropdownIsBuilded = this.items.length > 0;
    this.totalItems = this.cartDropdownIsBuilded;
    this.cartDropdownIsOpen = false;
    this.cartDiscounts = 0;
    this.cartDrawerEnabled = settings.cartDrawerEnabled;
    this.cartLimitErrorIsHidden = true;

    // Cart Events
    this.eventToggleCart();
    this.expandEvents();
    this.cartEvents();
    this.cartEventAdd();
    this.cartEventRemoveError();

    // Init quantity for fields
    this.initQuantity();

    this.customEventAddProduct();

    // Init estimate shipping calculator
    this.estimateShippingCalculator();

    // Attributes
    if (this.cartMessage.length > 0) {
      this.cartFreeLimitShipping = Number(this.cartMessage[0].getAttribute('data-limit')) * 100 * window.Shopify.currency.rate;
      this.subtotal = window.theme.subtotal;
      this.circumference = 28 * Math.PI; // radius - stroke * 4 * PI

      this.freeShippingMessageHandle(this.subtotal);
      this.updateProgress();
    }

    // Pair with
    this.pairWithArray = window.pairWithProducts;
    this.sessionStorage = window.sessionStorage;
    this.getAvailablePairProducts();
    this.renderPairProducts();
    this.skipPairProductEvent();

    document.addEventListener('theme:upsell:unavailable', () => {
      this.checkPairProductIsSoldOut();
    });
  }

  /**
   * Init quantity field functionality
   *
   * @return  {Void}
   */

  initQuantity() {
    this.items = document.querySelectorAll(selectors.item);

    this.items.forEach((item) => {
      const initQuantity = new QuantityCounter(item, true);

      initQuantity.init();
      this.customEventsHandle(item);
    });
  }

  /**
   * Expand blocks and close siblings
   *
   * @return  {Void}
   */

  expandEvents() {
    const holders = document.querySelectorAll(selectors.cartWidget);

    this.clickedElementForExpanding.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();

        const holder = document.querySelector(item.getAttribute('href'));

        item.classList.toggle(classes.active);
        slideToggle(holder, 400);

        if (holders.length > 1) {
          holders.forEach((content) => {
            if (content !== holder.parentElement) {
              const buttonExpand = content.querySelector(selectors.clickedElementForExpanding);

              buttonExpand.classList.remove(classes.active);

              slideUp(buttonExpand.nextElementSibling, 400);
            }
          });
        }
      });
    });
  }

  /**
   * Custom event who change the cart
   *
   * @return  {Void}
   */

  customEventsHandle(holder) {
    holder.addEventListener(
      'theme:cart:update',
      debounce((event) => {
        this.updateCart(
          {
            id: event.detail.id,
            quantity: event.detail.quantity,
          },
          holder,
          event.detail.valueIsEmpty
        );
      }, 500)
    );
  }

  /**
   *  Custom event for add product to the cart
   */
  customEventAddProduct() {
    this.html.addEventListener(
      'theme:cart:add',
      debounce((event) => {
        this.addToCart(`id=${event.detail.data.id}`, event.detail);
      }, 500)
    );
  }

  /**
   * Cart events
   *
   * @return  {Void}
   */

  cartEvents() {
    const cartItemRemove = document.querySelectorAll(selectors.cartItemRemove);

    cartItemRemove.forEach((item) => {
      item.addEventListener('click', (event) => {
        event.preventDefault();

        if (item.classList.contains(classes.disabled)) return;
        item.classList.add(classes.disabled);

        this.updateCart({
          id: item.dataset.id,
          quantity: 0,
        });
      });
    });

    if (this.cartCloseErrorMessage) {
      this.cartCloseErrorMessage.addEventListener('click', (event) => {
        event.preventDefault();

        slideUp(this.cartErrorHolder, 400);
      });
    }
  }

  /**
   * Cart event add product to cart
   *
   * @return  {Void}
   */

  cartEventAdd() {
    document.addEventListener('click', (event) => {
      const clickedElement = event.target;

      if (clickedElement.matches(selectors.buttonAddToCart) || (clickedElement.closest(selectors.buttonAddToCart) && clickedElement)) {
        let formData = '';
        let button = clickedElement.matches(selectors.buttonAddToCart) ? clickedElement : clickedElement.closest(selectors.buttonAddToCart);

        if (button.hasAttribute(selectors.productIDAttribute)) {
          formData = `id=${Number(button.getAttribute(selectors.productIDAttribute))}`;
        } else {
          this.form = clickedElement.closest('form');
          formData = new FormData(this.form);
          formData = new URLSearchParams(formData).toString();
        }

        if (this.form !== null && this.form.querySelector('[type="file"]')) {
          return;
        }

        event.preventDefault();

        if (clickedElement.hasAttribute('disabled') || clickedElement.parentNode.hasAttribute('disabled')) {
          return;
        }

        this.addToCart(formData, null, button);

        this.html.dispatchEvent(
          new CustomEvent('theme:cart:add-item', {
            bubbles: true,
            detail: {
              selector: clickedElement,
            },
          })
        );
      }
    });
  }

  /**
   * Cart event remove out of stock error
   *
   * @return  {Void}
   */

  cartEventRemoveError() {
    document.addEventListener('click', (event) => {
      const clickedElement = event.target;

      if (clickedElement.matches(selectors.formCloseError) || clickedElement.closest(selectors.formCloseError)) {
        event.preventDefault();

        const errorContainer = clickedElement.closest(selectors.formErrorsContainer);

        if (errorContainer) {
          errorContainer.classList.remove(classes.visible);
        }
      }
    });
  }

  /**
   * Estimate shippint calculator
   *
   * @return  {Void}
   */

  estimateShippingCalculator() {
    Shopify.Cart.ShippingCalculator.show({
      submitButton: theme.strings.shippingCalcSubmitButton,
      submitButtonDisabled: theme.strings.shippingCalcSubmitButtonDisabled,
      customerIsLoggedIn: theme.settings.customerLoggedIn,
      moneyFormat: theme.moneyWithCurrencyFormat,
    });
  }

  /**
   * Get response from the cart
   *
   * @return  {Void}
   */

  getCart() {
    fetch(theme.routes.root + 'cart.js')
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        this.updateCounter(response.item_count);
        this.newTotalItems = response.items.length;

        this.buildTotalPrice(response);
        this.freeShippingMessageHandle(response.total_price);

        if (this.cartMessage.length > 0) {
          this.subtotal = response.total_price;
          this.updateProgress();
        }

        return fetch(theme.routes.cart + '?section_id=api-cart-items');
      })
      .then((response) => response.text())
      .then((response) => {
        const element = document.createElement('div');
        element.innerHTML = response;

        if (element.querySelector(selectors.pairProductsScript)) {
          this.pairWithArray = JSON.parse(element.querySelector(selectors.pairProductsScript).innerText.split('=')[1].replace(';', ''));
        } else {
          this.pairWithArray = undefined;
        }

        this.getAvailablePairProducts();
        this.renderPairProducts();

        const cleanResponse = element.querySelector('[data-api-content]').innerHTML;
        this.build(cleanResponse);
      })
      .catch((error) => console.log(error));
  }

  /**
   * Add item(s) to the cart and show the added item(s)
   *
   * @param   {String}  data
   * @param   {DOM Element/Object}  quickAddHolder
   * @param   {DOM Element}  button
   *
   * @return  {Void}
   */

  addToCart(data, quickAddHolder = null, button = null, json = null) {
    // Get Quick Add form
    if (this.form === null && quickAddHolder !== null && quickAddHolder.label) {
      this.form = quickAddHolder.label.parentNode.querySelector('form');
    }

    if (this.cartDrawerEnabled && button) {
      button.classList.add(classes.loading);
    }

    fetch(theme.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: data,
    })
      .then((response) => response.json())
      .then((response) => {
        if (button) {
          button.setAttribute('disabled', 'disabled');
        }

        if (response.status) {
          if (quickAddHolder !== null) {
            this.addToCartError(response, quickAddHolder.element, button);
          } else {
            this.addToCartError(response, null, button);
          }

          return;
        }

        if (this.cartDrawerEnabled) {
          if (quickAddHolder !== null && quickAddHolder.label) {
            quickAddHolder.label.classList.remove(classes.hidden, classes.loading);
            quickAddHolder.label.classList.add(classes.added);
          }

          this.getCart();

          setTimeout(() => {
            if (button !== null) {
              button.classList.remove(classes.loading);
              button.removeAttribute('disabled');
              button.classList.add(classes.success);

              document.dispatchEvent(
                new CustomEvent('theme:product:button-update', {
                  detail: {
                    response: response,
                  },
                  bubbles: false,
                })
              );

              if (button.closest(selectors.pairProductsHolder)) {
                setTimeout(() => {
                  button.classList.remove(classes.success);
                }, settings.times.timeoutAddProduct * 2);
              }
            }

            if (this.cartDropdown) {
              this.openCartDropdown();
              this.cartDropdownIsOpen = true;
            }
          }, settings.times.timeoutAddProduct);
        } else {
          window.location = theme.routes.cart;
        }
      })
      .catch((error) => console.log(error));
  }

  /**
   * Update cart
   *
   * @param   {Object}  updateData
   *
   * @return  {Void}
   */

  updateCart(updateData = {}, holder = null, valueIsEmpty = false) {
    let newCount = null;
    let oldCount = null;
    let newItem = null;
    let settedQuantity = updateData.quantity;

    if (holder !== null) {
      holder.closest(selectors.item).classList.add(classes.loading);
    }

    this.items.forEach((item) => {
      const itemInput = item.querySelector('input');
      const itemButtons = item.querySelectorAll('button');

      item.classList.add(classes.disabled);
      itemInput.blur();
      itemInput.disabled = true;
      if (itemButtons.length) {
        itemButtons.forEach((itemButton) => {
          itemButton.disabled = true;
        });
      }
    });

    fetch(theme.routes.root + 'cart.js')
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        const matchKeys = (item) => item.key === updateData.id;
        const index = response.items.findIndex(matchKeys);
        oldCount = response.item_count;
        newItem = response.items[index].title;

        const data = {
          line: `${index + 1}`,
          quantity: settedQuantity,
        };

        return fetch(theme.routes.root + 'cart/change.js', {
          method: 'post',
          headers: {'Content-Type': 'application/json'},
          body: JSON.stringify(data),
        });
      })
      .then(this.handleErrors)
      .then((response) => response.json())
      .then((response) => {
        newCount = response.item_count;

        if (valueIsEmpty) {
          settedQuantity = 1;
        }

        if (settedQuantity !== 0) {
          this.cartLimitErrorIsHidden = newCount !== oldCount;

          this.toggleLimitError(newItem);
        }

        this.updateCounter(newCount);

        // Change the cart total and hide message if missing discounts and the changed product is not deleted
        this.buildTotalPrice(response);
        this.freeShippingMessageHandle(response.total_price);
        this.cartDiscounts = response.total_discount;

        // Build cart again if the quantity of the changed product is 0 or cart discounts are changed
        if (this.cartMessage.length > 0) {
          this.subtotal = response.total_price;
          this.updateProgress();
        }

        this.getCart();
      })
      .catch((error) => console.log(error));
  }

  /**
   * Show/hide limit error
   *
   * @param   {String}  itemTitle
   *
   * @return  {Void}
   */

  toggleLimitError(itemTitle) {
    this.cartErrorHolder.querySelector(selectors.errorMessage).innerText = itemTitle;

    if (this.cartLimitErrorIsHidden) {
      slideUp(this.cartErrorHolder, 400);
    } else {
      slideDown(this.cartErrorHolder, 400);
    }
  }

  /**
   * Handle errors
   *
   * @param   {Object}  response
   *
   * @return  {Object}
   */

  handleErrors(response) {
    if (!response.ok) {
      return response.json().then(function (json) {
        const e = new FetchError({
          status: response.statusText,
          headers: response.headers,
          json: json,
        });
        throw e;
      });
    }
    return response;
  }

  /**
   * Add to cart error handle
   *
   * @param   {Object}  data
   * @param   {DOM Element/Null} quickAddHolder
   * @param   {DOM Element/Null} button
   *
   * @return  {Void}
   */

  addToCartError(data, quickAddHolder, button) {
    let errorContainer = null;
    if (this.cartDrawerEnabled && button && button.closest(selectors.cartDropdown) !== null && !button.closest(selectors.cartDropdown)) {
      this.closeCartDropdown();
    }

    if (button !== null) {
      const outerContainer = button.closest(selectors.outerSection) || button.closest(selectors.upsellHolder) || button.closest(selectors.upsellModal);

      errorContainer = outerContainer?.querySelector(selectors.formErrorsContainer);

      button.classList.remove(classes.loading);
      button.removeAttribute('disabled');

      document.dispatchEvent(
        new CustomEvent('theme:product:button-update', {
          detail: {
            response: data,
          },
          bubbles: false,
        })
      );
    }

    if (errorContainer) {
      errorContainer.innerHTML = `<div class="errors">${data.message}: ${data.description}<span class="errors__close" data-close-error><svg aria-hidden="true" focusable="false" role="presentation" class="icon icon-close-thin" viewBox="0 0 27 27"><g stroke="#979797" fill="none" fill-rule="evenodd" stroke-linecap="square"><path d="M.5.5l26 26M26.5.5l-26 26"></path></g></svg></span></div>`;

      errorContainer.classList.add(classes.visible);

      document.dispatchEvent(new CustomEvent('theme:product:button-error', {bubbles: false}));
    }

    if (quickAddHolder) {
      this.html.dispatchEvent(
        new CustomEvent('theme:cart:error', {
          bubbles: true,
          detail: {
            message: data.message,
            description: data.description,
            holder: quickAddHolder,
          },
        })
      );
    }
  }

  /**
   * Open cart dropdown and add class on body
   *
   * @return  {Void}
   */

  openCartDropdown() {
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.cartDropdown}));
    document.dispatchEvent(new CustomEvent('theme:scroll:lock', {bubbles: true, detail: this.cartDropdownBody}));

    this.html.classList.add(classes.htmlClasses);
    this.cartDropdown.classList.add(classes.open);

    this.accessibility.trapFocus(this.cartDropdown, {
      elementToFocus: this.cartDropdown.querySelector(selectors.cartToggleElement),
    });

    if (!this.cartDropdownIsBuilded) {
      this.getCart();
    }

    // Observe Additional Checkout Buttons
    this.observeAdditionalCheckoutButtons();
  }

  /**
   * Close cart dropdown and remove class on body
   *
   * @return  {Void}
   */

  closeCartDropdown() {
    document.dispatchEvent(
      new CustomEvent('theme:cart:close', {
        bubbles: true,
      })
    );

    this.accessibility.removeTrapFocus();

    slideUp(this.cartErrorHolder, 400);

    if (this.body.classList.contains(classes.focused)) {
      const buttonOpenCart = this.headerWrapper.querySelectorAll(`${selectors.cartToggleElement}`);
      if (buttonOpenCart.length) {
        buttonOpenCart.forEach((button) => {
          if (isVisible(button)) {
            setTimeout(() => {
              button.focus();
            }, 200);

            return true;
          }
        });
      }
    }

    const upsellButton = document.querySelector(`[${attributes.upsellButton}].${classes.success}`);

    if (upsellButton) {
      setTimeout(() => {
        upsellButton.classList.remove(classes.success);
      }, 2000);
    }

    this.html.classList.remove(classes.htmlClasses);
    this.cartDropdown.classList.remove(classes.open);

    document.dispatchEvent(new CustomEvent('theme:scroll:unlock', {bubbles: true}));
  }

  /**
   * Toggle cart dropdown
   *
   * @return  {Void}
   */

  toggleCartDropdown() {
    this.cartDropdownIsOpen = !this.cartDropdownIsOpen;

    if (this.cartDropdownIsOpen) {
      this.openCartDropdown();
    } else {
      this.closeCartDropdown();
    }
  }

  /**
   * Event click to element to open cart dropdown
   *
   * @return  {Void}
   */

  eventToggleCart() {
    document.addEventListener('click', (event) => {
      const clickedElement = event.target;
      const isNotCartButton = !(clickedElement.matches(selectors.cartToggleElement) || clickedElement.closest(selectors.cartToggleElement));
      const isNotCartDropdownOrCartDropdownChild = !(clickedElement.matches(selectors.cartDropdown) || clickedElement.closest(selectors.cartDropdown));
      const isNotPairProduct = !(clickedElement.matches(selectors.buttonSkipPairProduct) || clickedElement.closest(selectors.buttonSkipPairProduct));

      if (clickedElement.matches(selectors.cartToggleElement) || clickedElement.closest(selectors.cartToggleElement)) {
        event.preventDefault();

        this.toggleCartDropdown();
      } else if (this.cartDropdownIsOpen && isNotCartButton && isNotCartDropdownOrCartDropdownChild && isNotPairProduct) {
        this.cartDropdownIsOpen = false;

        this.closeCartDropdown();
      }
    });

    if (this.cartDropdown) {
      this.cartDropdown.addEventListener('keyup', (event) => {
        if (event.which === window.theme.keyboardKeys.ESCAPE) {
          this.cartDropdownIsOpen = false;
          this.closeCartDropdown();
        }
      });
    }
  }

  /**
   * Toggle classes on different containers and messages
   *
   * @return  {Void}
   */

  toggleClassesOnContainers() {
    this.cartEmpty.classList.toggle(classes.hidden, this.hasItemsInCart());
    this.buttonHolder.classList.toggle(classes.hidden, !this.hasItemsInCart());
    this.itemsHolder.classList.toggle(classes.hidden, !this.hasItemsInCart());
  }

  /**
   * Build cart depends on results
   *
   * @param   {Object}  data
   *
   * @return  {Void}
   */

  build(data) {
    if (this.totalItems !== this.newTotalItems) {
      this.totalItems = this.newTotalItems;

      this.toggleClassesOnContainers();
    }

    this.itemsHolder.innerHTML = data;

    this.cartEvents();
    this.initQuantity();
  }

  /**
   * Update cart count
   *
   * @param   {Number}  countItems
   *
   * @return  {Void}
   */

  updateCounter(countItems) {
    if (!this.counterHolders.length) {
      return;
    }

    this.counterHolders.forEach((holder) => {
      holder.innerHTML = countItems < 10 ? countItems : '9+';
      holder.setAttribute(selectors.cartCountValue, countItems);
    });
  }

  /**
   * Check for items in the cart
   *
   * @return  {Void}
   */

  hasItemsInCart() {
    return this.totalItems > 0;
  }

  /**
   * Build total cart total price
   *
   * @param   {Object}  data
   *
   * @return  {Void}
   */

  buildTotalPrice(data) {
    if (data.original_total_price > data.total_price && data.cart_level_discount_applications.length > 0) {
      this.cartOriginalTotal.classList.remove(classes.hidden);
      this.cartOriginaTotalPrice.innerHTML = data.original_total_price === 0 ? window.theme.strings.free : formatMoney(data.original_total_price, theme.moneyFormat);
    } else {
      this.cartOriginalTotal.classList.add(classes.hidden);
    }

    this.cartTotal.innerHTML = data.total_price === 0 ? window.theme.strings.free : formatMoney(data.total_price, theme.moneyWithCurrencyFormat);

    if (data.cart_level_discount_applications.length > 0) {
      const discountsMarkup = this.buildCartTotalDiscounts(data.cart_level_discount_applications);

      this.cartDiscountHolder.classList.remove(classes.hidden);
      this.cartDiscountHolder.innerHTML = discountsMarkup;
    } else {
      this.cartDiscountHolder.classList.add(classes.hidden);
    }
  }

  /**
   * Build cart total discounts
   *
   * @param   {Array}  discounts
   *
   * @return  {String}
   */

  buildCartTotalDiscounts(discounts) {
    let discountMarkup = '';

    discounts.forEach((discount) => {
      discountMarkup += Sqrl.render(this.cartTotalDiscountTemplate, {
        discountTitle: discount.title,
        discountTotalAllocatedAmount: formatMoney(discount.total_allocated_amount, theme.moneyFormat),
      });
    });

    return discountMarkup;
  }

  /**
   * Show/hide free shipping message
   *
   * @param   {Number}  total
   *
   * @return  {Void}
   */

  freeShippingMessageHandle(total) {
    if (this.cartMessage.length > 0) {
      document.querySelectorAll(selectors.cartMessage).forEach((message) => {
        const hasQualifiedShippingMessage = message.hasAttribute(selectors.cartMessageValue) && message.getAttribute(selectors.cartMessageValue) === 'true' && total !== 0;

        message.classList.toggle(classes.success, hasQualifiedShippingMessage && total >= this.cartFreeLimitShipping);
        message.classList.toggle(classes.hidden, total === 0 || !hasQualifiedShippingMessage);
      });
    }
  }

  /**
   * Cart bar progress with message for free shipping
   *
   * @param   {Number}  progress
   *
   */
  cartBarProgress(progress = null) {
    document.querySelectorAll(selectors.cartProgress).forEach((element) => {
      this.setProgress(element, progress === null ? element.getAttribute('data-percent') : progress);
    });
  }

  /**
   * Set circle progress
   *
   * @param   {DOM Element}  holder
   * @param   {Number}       percent
   *
   * @return  {Void}
   */

  setProgress(holder, percent) {
    const offset = this.circumference - ((percent / 100) * this.circumference) / 2;

    holder.style.strokeDashoffset = offset;
  }

  /**
   * Update progress when update cart
   *
   * @return  {Void}
   */

  updateProgress() {
    const newPercentValue = (this.subtotal / this.cartFreeLimitShipping) * 100;
    const leftToSpend = formatMoney(this.cartFreeLimitShipping - this.subtotal, theme.moneyFormat);

    document.querySelectorAll(selectors.leftToSpend).forEach((element) => {
      element.innerHTML = leftToSpend;
    });

    this.cartBarProgress(newPercentValue > 100 ? 100 : newPercentValue);
  }

  /**
   * Render pair with products
   */
  renderPairProducts() {
    if (this.pairProductsHolder === null) {
      return;
    }

    if (this.pairWithArray === undefined || this.pairWithArray.length <= 0) {
      this.pairProductsHolder.classList.add(classes.hidden);

      return;
    }

    new PairWithProduct(this.pairWithArray[0]);

    document.addEventListener('theme:upsell:fetch-error', () => {
      this.checkPairProductIsSoldOut();
    });

    this.pairProductsHolder.classList.remove(classes.hidden);
  }

  /**
   * Update pairWithArray only with uniq and available handles
   */
  getAvailablePairProducts() {
    if (this.pairWithArray !== undefined && this.sessionStorage !== undefined && this.sessionStorage.getItem('pair_products') !== null) {
      this.pairWithArray = this.pairWithArray.filter((handle) => {
        return this.sessionStorage.getItem('pair_products').indexOf(`,${handle},`) === -1;
      });
    }
  }

  /**
   * Skip pair product
   */
  skipPairProductEvent() {
    if (this.pairProductsHolder === null) {
      return;
    }

    this.pairProductsHolder.addEventListener('click', (event) => {
      const target = event.target;

      if (target.matches(selectors.buttonSkipPairProduct) || target.closest(selectors.buttonSkipPairProduct)) {
        event.preventDefault();

        // Add to session storage
        if (this.sessionStorage !== undefined) {
          this.sessionStorage.setItem(
            'pair_products',
            this.sessionStorage.getItem('pair_products') !== null ? `${this.sessionStorage.getItem('pair_products')},${this.pairWithArray[0]},` : `,${this.pairWithArray[0]},`
          );
        }

        this.getAvailablePairProducts();

        if (this.pairWithArray.length <= 0) {
          this.pairProductsHolder.classList.add(classes.hidden);
        } else {
          new PairWithProduct(this.pairWithArray[0]);
        }
      }
    });
  }

  /**
   * Check pair product is sold out
   */
  checkPairProductIsSoldOut() {
    if (this.sessionStorage !== undefined) {
      this.sessionStorage.setItem(
        'pair_products',
        this.sessionStorage.getItem('pair_products') !== null ? `${this.sessionStorage.getItem('pair_products')},${this.pairWithArray[0]},` : `,${this.pairWithArray[0]},`
      );

      this.getAvailablePairProducts();

      if (this.pairWithArray.length <= 0) {
        this.pairProductsHolder.classList.add(classes.hidden);
      } else {
        this.renderPairProducts();
      }
    }
  }

  observeAdditionalCheckoutButtons() {
    // identify an element to observe
    const additionalCheckoutButtons = this.cartDropdown.querySelector(selectors.additionalCheckoutButtons);
    if (additionalCheckoutButtons) {
      // create a new instance of `MutationObserver` named `observer`,
      // passing it a callback function
      const observer = new MutationObserver(() => {
        this.accessibility.removeTrapFocus();
        this.accessibility.trapFocus(this.cartDropdown, {
          elementToFocus: this.cartDropdown.querySelector(selectors.cartToggleElement),
        });
        observer.disconnect();
      });

      // call `observe()` on that MutationObserver instance,
      // passing it the element to observe, and the options object
      observer.observe(additionalCheckoutButtons, {subtree: true, childList: true});
    }
  }
}

window.cart = new CartDrawer();
